import {
  Page,
  Button,
  LegacyCard,
  Text,
  FormLayout,
  TextField,
  Select,
  BlockStack,
  Modal,
  Icon,
  InlineGrid,
  Autocomplete,
} from "@shopify/polaris";
import { HideIcon, ViewIcon, SearchIcon } from "@shopify/polaris-icons";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import AccountAlert from "../components/atoms/AccountAlert";
import { useCreateAccountMutation } from "../store/store/slices/AccountRTK";
import { useDispatch } from "react-redux";
import { showToast } from "../store/store/slices/AlertSlice";
import { useFetchTcsCitiesQuery } from "../store/store/slices/CitiesRTK";
import { CITY_OPTIONS_LIST, CITY_TYPE } from "../store/types/BookOrder";
import { escapeRegExp } from "../utils/helpers";

type FormData = {
  account_name: string;
  username?: string;
  account_no?: string;
  api_password?: string;
  api_key?: string;
  token?: string;
  is_default?: string;
  weight: string;
  address_code?: string;
  shipper_name?: string;
  shipper_mobile?: string;
  shipper_address?: string;
  cost_center_code: string;
  client_id?: string;
  client_secret?: string;
  tcs_accout_id: string;
  pickup_address_id?: string;
};

export default function Settings() {
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [options, setOptions] = useState<CITY_OPTIONS_LIST[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showSecret, setShowSecret] = useState<boolean>(false);
  const [company, setCompany] = useState<string>("");
  const [planError, setPlanError] = useState<{
    error: boolean;
    message: string;
  }>({ error: false, message: "" });
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>();
  const {
    data: cities,
    isLoading: cityFetchingLoading,
    isError: isCityFetchingFailed,
    isFetching: citiesRefetching,
  } = useFetchTcsCitiesQuery(undefined);

  const [
    createAccount,
    {
      isLoading: creating,
      error: creatingError,
      isSuccess: accountCreatingSuccess,
      data: accountCreateData,
    },
  ] = useCreateAccountMutation();
  const redirectUserToPlans = async () => {
    navigateTo("/dashboard/plans");
  };
  const onSubmit = async (formData: FormData) => {
    if (company.trim() === "") {
      return;
    }
    const accountData = { ...formData, company, shipper_city: inputValue };
    await createAccount(accountData).unwrap();
    reset();
  };
  const onShowPasswordHandler = async () => {
    setShowPassword((prev) => !prev);
  };
  const onShowSecretdHandler = async () => {
    setShowSecret((prev) => !prev);
  };

  useEffect(() => {
    if (accountCreateData?.plan_limit_error) {
      setPlanError({
        error: accountCreateData?.plan_limit_error,
        message: accountCreateData?.message || "Kindly upgrade your plan",
      });
    } else {
      if (accountCreatingSuccess) {
        dispatch(
          showToast({
            message: "Account has been created",
            error: false,
            visible: true,
          })
        );
        setPlanError({
          error: false,
          message: "",
        });
        setTimeout(() => {
          navigateTo("/dashboard/accounts");
        }, 700);
      } else if (creatingError) {
        dispatch(
          showToast({
            message: creatingError?.data?.errors[0]?.msg,
            error: true,
            visible: true,
          })
        );
      }
    }
  }, [creatingError, creating, accountCreateData]);

  useEffect(() => {
    setOptions(cities);
    // const city = cities?.filter(
    //   (city: CITY_TYPE) =>
    //     city?.label?.toLowerCase() ===
    //     fetchedShopifyOrder?.consignee_city?.toLowerCase()
    // );
    // if (city?.length > 0) {
    //   updateSelection([city[0]?.value]);
    // } else {
    //   updateSelection([""]);
    // }
  }, [cities]);

  const updateText = useCallback(
    (value: string) => {
      setInputValue(value);
      if (value === "") {
        setOptions(cities);
        return;
      }
      const filterRegex = new RegExp(escapeRegExp(value), "i");
      const resultOptions = cities.filter((option: CITY_TYPE) =>
        option.label.match(filterRegex)
      );
      setOptions(resultOptions);
    },
    [cities]
  );

  const updateSelection = useCallback(
    (selected: string[]) => {
      const selectedId = selected[0];
      const matchedOption = cities?.find((option: CITY_TYPE) => {
        if (typeof option?.value === "number")
          return option?.value === Number(selectedId);
        return option?.value === selectedId;
      });
      if (matchedOption) {
        setSelectedOptions([matchedOption.value]);
        setInputValue(matchedOption.label);
      } else {
        setSelectedOptions([]);
        setInputValue("");
      }
    },
    [cities]
  );
  const textField = (
    <Autocomplete.TextField
      onChange={updateText}
      name="shipper_city"
      label="City"
      value={inputValue}
      prefix={<Icon source={SearchIcon} tone="base" />}
      placeholder="Search"
      autoComplete="off"
      disabled={cityFetchingLoading || citiesRefetching || isCityFetchingFailed}
      loading={cityFetchingLoading || citiesRefetching}
      requiredIndicator
      error={
        isCityFetchingFailed
          ? "City fetching failed"
          : inputValue?.trim() === ""
            ? "Please add destination city"
            : ""
      }
    />
  );

  return (
    <Page
      backAction={{
        content: "Settings",
        url: "",
        onAction: () => navigateTo(-1),
      }}
      title="Settings"
      primaryAction={
        <Button
          variant={theme === "dark" ? "secondary" : "primary"}
          onClick={handleSubmit(onSubmit)}
          disabled={creating}
          loading={creating}
        >
          Save
        </Button>
      }
    >
      <BlockStack gap="500">
        <Text as="h1" fontWeight="bold">
          <span style={{ fontSize: "20px" }}>TRACKING CREDENTIALS</span>
        </Text>
        <AccountAlert />
        <FormLayout>
          <LegacyCard sectioned>
            <Select
              label="Select courier"
              options={[
                { label: "Select a company", value: "" },
                { label: "MNP", value: "MNP" },
                { label: "LEOPARD", value: "LEOPARD" },
                { label: "POSTEX", value: "POSTEX" },
                { label: "TCS", value: "TCS" },
                { label: "TRAX", value: "TRAX" },
              ]}
              onChange={(val) => setCompany(val)}
              value={company}
              requiredIndicator
              disabled={creating}
              error={company === "" ? "Company name is required" : ""}
            />
          </LegacyCard>
          {company && (
            <LegacyCard sectioned>
              <BlockStack gap="400">
                <Controller
                  control={control}
                  name="account_name"
                  rules={{ required: true, maxLength: 30 }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      label="Account name: You can write any name of your choice to uniquely identify your account"
                      autoComplete="Off"
                      value={value || ""}
                      onChange={onChange}
                      placeholder="Enter account name"
                      disabled={creating}
                      requiredIndicator
                      error={
                        errors.account_name
                          ? "Account name is required and max length 30"
                          : ""
                      }
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="weight"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      label="Weight: (in grams) This weight will displayed on order booking page so you don't have to add manually"
                      autoComplete="Off"
                      value={value || ""}
                      onChange={onChange}
                      type="number"
                      max={50000}
                      placeholder="Enter default weight"
                      min={0}
                      disabled={creating}
                      requiredIndicator
                      error={
                        errors.weight
                          ? "Weight is required and max limit 50,000"
                          : ""
                      }
                    />
                  )}
                />
                {company === "MNP" && (
                  <>
                    <Controller
                      control={control}
                      name="username"
                      rules={{ required: true, maxLength: 100 }}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label="M&P Username"
                          autoComplete="Off"
                          value={value || ""}
                          onChange={onChange}
                          placeholder="Enter username provided by M&P"
                          disabled={creating}
                          requiredIndicator
                          error={
                            errors.username
                              ? "Username is required and max length 100"
                              : ""
                          }
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="account_no"
                      rules={{ required: true, maxLength: 50 }}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label="Account number: Given by m&p, you can find it on mnp dashboard > reports > consignee profile"
                          value={value || ""}
                          autoComplete="Off"
                          onChange={onChange}
                          placeholder="Enter account number"
                          disabled={creating}
                          requiredIndicator
                          error={
                            errors.account_no
                              ? "Account number is required and max length 50"
                              : ""
                          }
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="api_password"
                      rules={{ required: true, maxLength: 100 }}
                      render={({ field: { onChange, value } }) => (
                        <div style={{ position: "relative" }}>
                          <span
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "20px",
                              padding: "0",
                              position: "absolute",
                              right: "5px",
                              top: "30px",
                              zIndex: "100000",
                              cursor: "pointer",
                            }}
                            onClick={onShowPasswordHandler}
                          >
                            <Icon
                              source={!showPassword ? ViewIcon : HideIcon}
                            />
                          </span>
                          <TextField
                            autoComplete="off"
                            label="M&P Password"
                            value={value || ""}
                            type={showPassword ? "text" : "password"}
                            onChange={onChange}
                            placeholder="Enter password provided by M&P"
                            disabled={creating}
                            requiredIndicator
                            error={
                              errors.api_password
                                ? "API password is required max 100 characters"
                                : ""
                            }
                          ></TextField>{" "}
                        </div>
                      )}
                    />
                  </>
                )}

                {company === "LEOPARD" && (
                  <>
                    <Controller
                      control={control}
                      name="api_key"
                      rules={{ required: true, maxLength: 500 }}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label="Api Key"
                          value={value || ""}
                          autoComplete="Off"
                          onChange={onChange}
                          placeholder="Enter API key provided by leopard"
                          disabled={creating}
                          requiredIndicator
                          error={
                            errors.api_key
                              ? "API key is required (max 500 characters)"
                              : ""
                          }
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="api_password"
                      rules={{ required: true, maxLength: 100 }}
                      render={({ field: { onChange, value } }) => (
                        <div style={{ position: "relative" }}>
                          <span
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "20px",
                              padding: "0",
                              position: "absolute",
                              right: "5px",
                              top: "30px",
                              zIndex: "100000",
                              cursor: "pointer",
                            }}
                            onClick={onShowPasswordHandler}
                          >
                            <Icon
                              source={!showPassword ? ViewIcon : HideIcon}
                            />
                          </span>
                          <TextField
                            autoComplete="off"
                            label="Password"
                            value={value || ""}
                            type={showPassword ? "text" : "password"}
                            onChange={onChange}
                            placeholder="Enter password provided by leopard"
                            disabled={creating}
                            requiredIndicator
                            error={
                              errors.api_password
                                ? "API password is required and (max 100 characters)"
                                : ""
                            }
                          ></TextField>{" "}
                        </div>
                      )}
                    />
                  </>
                )}
                {company === "TCS" && (
                  <>
                    <Controller
                      control={control}
                      name="username"
                      rules={{ required: true, maxLength: 100 }}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label="TCS Username"
                          autoComplete="Off"
                          value={value || ""}
                          onChange={onChange}
                          placeholder="Enter username provided by TCS"
                          disabled={creating}
                          requiredIndicator
                          error={
                            errors.username
                              ? "Username is required and max length 100"
                              : ""
                          }
                        />
                      )}
                    />
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "20px",
                          padding: "0",
                          position: "absolute",
                          right: "5px",
                          top: "30px",
                          zIndex: "100000",
                          cursor: "pointer",
                        }}
                        onClick={onShowPasswordHandler}
                      >
                        <Icon source={!showPassword ? ViewIcon : HideIcon} />
                      </span>
                      <Controller
                        control={control}
                        name="api_password"
                        rules={{ required: true, maxLength: 100 }}
                        render={({ field: { onChange, value } }) => (
                          <TextField
                            label="Tcs password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="Off"
                            value={value || ""}
                            onChange={onChange}
                            placeholder="Enter password provided by TCS"
                            disabled={creating}
                            requiredIndicator
                            error={
                              errors.api_password
                                ? "Password is required and max length 100"
                                : ""
                            }
                          />
                        )}
                      />
                    </div>
                    <Controller
                      control={control}
                      name="tcs_account_id"
                      rules={{ required: true, maxLength: 100 }}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label="Tcs account id"
                          autoComplete="Off"
                          value={value || ""}
                          onChange={onChange}
                          placeholder="Enter accoount id provided by TCS"
                          disabled={creating}
                          requiredIndicator
                          error={
                            errors.api_password
                              ? "Tcs account id is required and max length 100"
                              : ""
                          }
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="client_id"
                      rules={{ required: true, maxLength: 500 }}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label="Client id"
                          value={value || ""}
                          autoComplete="Off"
                          onChange={onChange}
                          placeholder="Enter client id"
                          disabled={creating}
                          requiredIndicator
                          error={
                            errors.api_key
                              ? "Client id is required (max 500 characters)"
                              : ""
                          }
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="client_secret"
                      rules={{ required: true, maxLength: 100 }}
                      render={({ field: { onChange, value } }) => (
                        <div style={{ position: "relative" }}>
                          <span
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "20px",
                              padding: "0",
                              position: "absolute",
                              right: "5px",
                              top: "30px",
                              zIndex: "100000",
                              cursor: "pointer",
                            }}
                            onClick={onShowSecretdHandler}
                          >
                            <Icon source={!showSecret ? ViewIcon : HideIcon} />
                          </span>
                          <TextField
                            autoComplete="off"
                            label="Client secret"
                            value={value || ""}
                            type={showSecret ? "text" : "password"}
                            onChange={onChange}
                            placeholder="Enter client secret"
                            disabled={creating}
                            requiredIndicator
                            error={
                              errors.client_secret
                                ? "client secret is required and (max 100 characters)"
                                : ""
                            }
                          />
                        </div>
                      )}
                    />
                    <Controller
                      control={control}
                      name="cost_center_code"
                      rules={{ required: true, maxLength: 100 }}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label="TCS Cost Center Code"
                          autoComplete="Off"
                          value={value}
                          onChange={onChange}
                          placeholder="Enter Cost Center Code provided by TCS"
                          disabled={creating}
                          requiredIndicator
                          error={
                            errors.cost_center_code &&
                            "Cost Center Code is required and max length 100"
                          }
                        />
                      )}
                    />
                  </>
                )}

                {company === "POSTEX" && (
                  <>
                    <Controller
                      control={control}
                      name="token"
                      rules={{ required: true, maxLength: 500 }}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label="Token"
                          value={value || ""}
                          autoComplete="Off"
                          onChange={onChange}
                          placeholder="Enter postex token"
                          disabled={creating}
                          requiredIndicator
                          error={
                            errors.token ? "Token is required (max 500)" : ""
                          }
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="address_code"
                      rules={{ required: true, maxLength: 50 }}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label="Pickup Address ID"
                          value={value || ""}
                          autoComplete="Off"
                          onChange={onChange}
                          placeholder="Enter postex address code"
                          disabled={creating}
                          requiredIndicator
                          error={
                            errors.address_code
                              ? "Address code is required (max 50)"
                              : ""
                          }
                        />
                      )}
                    />
                  </>
                )}
                {company === "TRAX" && (
                  <>
                    <Controller
                      control={control}
                      name="api_key"
                      rules={{ required: true, maxLength: 500 }}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label="Api Key"
                          value={value || ""}
                          autoComplete="Off"
                          onChange={onChange}
                          placeholder="Enter API key provided by leopard"
                          disabled={creating}
                          requiredIndicator
                          error={
                            errors.api_key
                              ? "API key is required (max 500 characters)"
                              : ""
                          }
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="pickup_address_id"
                      rules={{ required: true, maxLength: 500 }}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label="Pickup address id"
                          value={value || ""}
                          autoComplete="Off"
                          onChange={onChange}
                          placeholder="Enter pickup address id provided by TRAX"
                          disabled={creating}
                          requiredIndicator
                          error={
                            errors.pickup_address_id
                              ? "Pickup address code is required (max 500 characters)"
                              : ""
                          }
                        />
                      )}
                    />
                  </>
                )}

                <Controller
                  control={control}
                  name="is_default"
                  defaultValue={"false"}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      label="Set As Default: Default account will be automatically selected on booking page"
                      onChange={onChange}
                      value={value || "false"}
                      options={[
                        { label: "No", value: "false" },
                        { label: "Default", value: "default" },
                      ]}
                      disabled={creating}
                    />
                  )}
                />
              </BlockStack>
            </LegacyCard>
          )}
          {company === "TCS" && (
            <LegacyCard
              sectioned
              title={
                <Text as="span" fontWeight="bold">
                  Shipper info{" "}
                  <span style={{ fontWeight: "400" }}>Required for TCS</span>
                </Text>
              }
            >
              <InlineGrid columns={{ xs: 1, lg: 2 }} gap={"400"}>
                <Controller
                  control={control}
                  name="shipper_name"
                  rules={{ required: true, maxLength: 100 }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      label="Shipper name"
                      autoComplete="Off"
                      value={value || ""}
                      onChange={onChange}
                      placeholder="Enter shipper name"
                      disabled={creating}
                      requiredIndicator
                      error={
                        errors.shipper_name
                          ? "Shipper name is required and max length 100"
                          : ""
                      }
                    />
                  )}
                />
                <Autocomplete
                  options={options || []}
                  selected={selectedOptions}
                  onSelect={updateSelection}
                  textField={textField}
                />
                <Controller
                  control={control}
                  name="shipper_address"
                  rules={{ required: true, maxLength: 100 }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      label="Shipper address"
                      autoComplete="Off"
                      value={value || ""}
                      onChange={onChange}
                      placeholder="Enter shipper address"
                      disabled={creating}
                      requiredIndicator
                      error={
                        errors.shipper_address
                          ? "Shipper address is required for TCS and max length 100"
                          : ""
                      }
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="shipper_mobile"
                  rules={{ required: true, maxLength: 100 }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      label="Shipper mobile"
                      autoComplete="Off"
                      value={value || ""}
                      onChange={onChange}
                      placeholder="Enter shipper mobile"
                      disabled={creating}
                      requiredIndicator
                      error={
                        errors.shipper_mobile
                          ? "Shipper mobile number is required for TCS and max length 100"
                          : ""
                      }
                    />
                  )}
                />
              </InlineGrid>
            </LegacyCard>
          )}
        </FormLayout>
      </BlockStack>
      <Modal
        onClose={redirectUserToPlans}
        open={planError?.error}
        title="Plan limit reached"
        primaryAction={{
          content: "Okay",
          onAction: () => redirectUserToPlans(),
        }}
      >
        <Modal.Section>{planError?.message}</Modal.Section>
      </Modal>
    </Page>
  );
}
